import { renderDateMacros } from '@orn/global/assistant/dateMacro.js';
import formatCompactTimeAgo from '@orn/shared-web/utils/datetime/formatCompactTimeAgo';

import type { AlertEntityRefs, AlertRowStatus, AlertRowSubType, AlertRowType } from './alerts';
import type { AlertListItemStatus } from '../../components/AlertListItem';

/** The fields a row's text derives from. */
export type AlertRowFields = {
  type: AlertRowType;
  title?: string | null;
  body?: string | null;
  status: AlertRowStatus;
  eventDate: string;
} & AlertEntityRefs;

/**
 * A row any alert list can render: the row's text fields, its identity, and
 * the subtype `getAlertRowAction` needs to pick a `CONFIG` alert's fix.
 */
export type ListedAlert = AlertRowFields & { id: string; subType?: AlertRowSubType | null };

/** `AlertListItem` props derived from an alert row — see {@link listedAlertToAlertRow}. */
export interface IAlertRow {
  title: string;
  subtitle: string | null;
  timestamp: string;
  status: AlertListItemStatus;
  tone: 'default' | 'error';
}

/** A new `AlertRowStatus` does not compile until it picks a badge. */
export const ALERT_ITEM_STATUS_BY_ROW_STATUS: Record<AlertRowStatus, AlertListItemStatus> = {
  UNREAD: 'unread',
  READ: 'read',
  PENDING: 'pending',
  ARCHIVED: 'resolved',
};

/**
 * A new `AlertRowType` does not compile until it picks a tone. `CONFIG` is the
 * error: it asks the user to fix something before the product works.
 */
export const ALERT_TONE_BY_TYPE: Record<AlertRowType, 'default' | 'error'> = {
  CONFIG: 'error',
  REMINDER: 'default',
  UPDATE: 'default',
};

interface IAlertRowText {
  title: string;
  subtitle: string | null;
}

/**
 * Producers author every row's `title` / `body` directly. The type-named
 * fallback covers malformed or stale rows rather than any intended shape;
 * empty and whitespace-only strings fall back too, since a blank title is as
 * unrenderable as a missing one.
 */
const authoredText =
  (fallbackTitle: string) =>
  (alert: AlertRowFields): IAlertRowText => ({
    title: alert.title?.trim() || fallbackTitle,
    subtitle: alert.body?.trim() || null,
  });

/** The fallback for a row that carries no authored `title`. */
const ROW_TEXT_BY_TYPE: Record<AlertRowType, (alert: AlertRowFields) => IAlertRowText> = {
  UPDATE: authoredText('Update'),
  REMINDER: authoredText('Reminder'),
  CONFIG: authoredText('Action needed'),
};

/**
 * Turns an alert row into the props an `AlertListItem` renders. The row itself
 * is presentational, so every type-to-text, status, and tone decision lands
 * here. A row carrying a `title` renders it verbatim; {@link ROW_TEXT_BY_TYPE}
 * is the fallback for rows that have none.
 *
 * @param alert - the alert (or work-item projection) the row derives from.
 * @param now - reference instant for the relative timestamp and the date-macro
 *   substitution. Defaults to the current time; pass a fixed date to make a
 *   test or story deterministic.
 * @param timeZone - zone the date-macro substitution reads its calendar day
 *   in. Defaults to the browser's; pass a fixed zone for determinism.
 * @returns the presentational props an `AlertListItem` renders.
 */
export function listedAlertToAlertRow(alert: AlertRowFields, now?: Date, timeZone?: string): IAlertRow {
  const authoredTitle = alert.title?.trim();
  const { title, subtitle } = authoredTitle
    ? { title: authoredTitle, subtitle: alert.body?.trim() || null }
    : ROW_TEXT_BY_TYPE[alert.type](alert);

  // Authored copy may carry `<date=… />`, which renders here as plain text —
  // substitution against the viewer's clock is what keeps a stored "today"
  // true. Fallback copy never carries one, so this is a pass-through there.
  const dateMacroContext = {
    now: now ?? new Date(),
    timeZone: timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  return {
    title: renderDateMacros(title, dateMacroContext),
    subtitle: subtitle === null ? null : renderDateMacros(subtitle, dateMacroContext),
    timestamp: formatCompactTimeAgo(new Date(alert.eventDate), now),
    status: ALERT_ITEM_STATUS_BY_ROW_STATUS[alert.status],
    tone: ALERT_TONE_BY_TYPE[alert.type],
  };
}
